import React, { useState } from 'react';
import {
  Box, List, ListItem, ListItemText, Typography,
  Avatar, Chip, Tooltip, IconButton, CircularProgress, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, FormControl, InputLabel, Select, MenuItem, Divider, TextField
} from '@mui/material';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { swalConfirmDelete } from '../../../utils/swal';

const ConnectionsView = ({
  connections = [],
  circles = [],
  onAddToCircle,          // (connection, circle, shareHistory, historyDays) => Promise
  onRemoveMemberFromCircle,
  onDisconnectMember,
}) => {
  const [addingConn,      setAddingConn]      = useState(null);
  const [addCircleId,     setAddCircleId]     = useState('');
  const [shareHistory,    setShareHistory]    = useState(false);
  const [historyDays,     setHistoryDays]     = useState('');
  const [addBusy,         setAddBusy]         = useState(false);

  const [removingKey,     setRemovingKey]     = useState('');
  const [disconnectingId, setDisconnectingId] = useState(null);

  const memberCircles = (customerId) =>
    circles.filter(c => (c.membersData || []).some(m => m.id === customerId));

  const availableCircles = (customerId) =>
    circles.filter(c => !(c.membersData || []).some(m => m.id === customerId));

  const toMember = (conn) => ({
    id: conn.customer_id,
    name: conn.customer_name,
    username: conn.username,
    profile_picture: conn.profile_picture,
  });

  const closeAddDialog = () => {
    setAddingConn(null);
    setAddCircleId('');
    setShareHistory(false);
    setHistoryDays('');
  };

  const handleAddConfirm = async () => {
    if (!addingConn || !addCircleId) return;
    const circle = circles.find(c => c.id === addCircleId);
    if (!circle || !onAddToCircle) return;
    setAddBusy(true);
    try {
      const days = shareHistory && historyDays ? parseInt(historyDays, 10) : null;
      await onAddToCircle(addingConn, circle, shareHistory, days);
      closeAddDialog();
    } finally {
      setAddBusy(false);
    }
  };

  const handleRemoveFromCircle = async (conn, circle) => {
    if (!onRemoveMemberFromCircle) return;
    const key = `${conn.customer_id}-${circle.id}`;
    setRemovingKey(key);
    try {
      await onRemoveMemberFromCircle(toMember(conn), circle);
    } finally {
      setRemovingKey('');
    }
  };

  const handleDisconnect = async (conn) => {
    const confirmed = await swalConfirmDelete({
      title: `Disconnect ${conn.customer_name}?`,
      text: 'They will be removed from all your circles.',
      confirmText: 'Disconnect',
    });
    if (!confirmed) return;
    setDisconnectingId(conn.customer_id);
    try {
      await onDisconnectMember(toMember(conn));
    } finally {
      setDisconnectingId(null);
    }
  };

  if (connections.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No connections yet. Search for people and send a connection request to get started.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="caption" color="text.secondary" sx={{ px: 1, mb: 1, display: 'block' }}>
        {connections.length} accepted connection{connections.length !== 1 ? 's' : ''}
      </Typography>

      <List disablePadding>
        {connections.map((conn, idx) => {
          const inCircles = memberCircles(conn.customer_id);
          const free      = availableCircles(conn.customer_id);
          const isDisconnecting = disconnectingId === conn.customer_id;

          return (
            <React.Fragment key={conn.customer_id}>
              {idx > 0 && <Divider />}
              <ListItem
                sx={{ py: 1.5, px: 1.5, '&:hover': { bgcolor: 'action.hover' } }}
                secondaryAction={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {free.length > 0 && (
                      <Tooltip title="Add to a circle">
                        <span>
                          <IconButton
                            size="small"
                            disabled={isDisconnecting}
                            onClick={() => { setAddingConn(conn); setAddCircleId(''); setShareHistory(false); setHistoryDays(''); }}
                          >
                            <ManageAccountsIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="Disconnect">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={isDisconnecting}
                          onClick={() => handleDisconnect(conn)}
                        >
                          {isDisconnecting
                            ? <CircularProgress size={16} />
                            : <PersonRemoveIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                }
              >
                <Avatar sx={{ width: 40, height: 40, mr: 2, flexShrink: 0 }}>
                  {(conn.customer_name || '?')[0].toUpperCase()}
                </Avatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {conn.customer_name}
                      </Typography>
                      {conn.username && (
                        <Typography variant="body2" color="text.secondary">
                          @{conn.username}
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    inCircles.length > 0 ? (
                      <Box sx={{ mt: 0.5 }}>
                        {inCircles.map(circle => (
                          <Tooltip key={circle.id} title={`Remove from ${circle.name}`}>
                            <Chip
                              label={circle.name}
                              size="small"
                              onDelete={() => handleRemoveFromCircle(conn, circle)}
                              disabled={removingKey === `${conn.customer_id}-${circle.id}`}
                              sx={{
                                mr: 0.5, mb: 0.5,
                                bgcolor: (circle.color || '#4ECDC4') + '30',
                                color: circle.color || '#4ECDC4',
                              }}
                            />
                          </Tooltip>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Not in any circle yet
                      </Typography>
                    )
                  }
                />
              </ListItem>
            </React.Fragment>
          );
        })}
      </List>

      {/* Add to circle - circle picker + history sharing in one screen */}
      <Dialog open={!!addingConn} onClose={() => { if (!addBusy) closeAddDialog(); }} maxWidth="xs" fullWidth>
        <DialogTitle>Add {addingConn?.customer_name} to a circle</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>

          <FormControl fullWidth size="small">
            <InputLabel>Circle</InputLabel>
            <Select
              value={addCircleId}
              onChange={(e) => setAddCircleId(e.target.value)}
              label="Circle"
            >
              {availableCircles(addingConn?.customer_id ?? -1).map(c => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider />

          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              Share event history?
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Choose which past events shared in this circle they can see.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant={!shareHistory ? 'contained' : 'outlined'}
                onClick={() => { setShareHistory(false); setHistoryDays(''); }}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                New events only
              </Button>
              <Button
                size="small"
                variant={shareHistory ? 'contained' : 'outlined'}
                onClick={() => setShareHistory(true)}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Share history
              </Button>
            </Box>
            {shareHistory && (
              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <TextField
                  size="small"
                  type="number"
                  placeholder="All history"
                  label="Past days"
                  value={historyDays}
                  onChange={(e) => setHistoryDays(e.target.value.replace(/[^0-9]/g, ''))}
                  InputProps={{ inputProps: { min: 1 } }}
                  sx={{ width: 140 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {historyDays
                    ? `Events from the past ${historyDays} day${historyDays === '1' ? '' : 's'}`
                    : 'Leave blank to share all past history'}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button disabled={addBusy} onClick={closeAddDialog} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!addCircleId || addBusy}
            onClick={handleAddConfirm}
            sx={{ textTransform: 'none' }}
          >
            {addBusy ? <CircularProgress size={18} /> : 'Add to Circle'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConnectionsView;
